import { useRef, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import registerSplash from '@/assets/register-splash.png'
import parentExplainerPoster from '@/assets/parent-explainer-poster.jpg'
import { sendVideoStartEvent, sendVideoProgressEvent, sendVideoCompleteEvent } from '@/services/analytics/gtm'

// Shared by the image and the video so the frame keeps the same footprint either way.
const MEDIA_CLASSES = 'w-full object-cover aspect-video rounded-md shadow-neutral-900 shadow-md'

// Identifies this video in analytics.
const PARENT_EXPLAINER_VIDEO_ID = 'parent_explainer'

// Reported once each per page load. 100% is covered by video_complete instead.
const PROGRESS_MILESTONES = [25, 50, 75]

/**
 * The media panel to the left of the registration form.
 *
 * Renders the Parent Explainer video when VITE_SHOW_VIDEO_ON_REGISTRATION is
 * enabled and a video URL is configured, otherwise falls back to the splash image.
 */
export default function RegistrationHero() {
  const { t } = useTranslation()
  const hasStarted = useRef(false)
  const hasCompleted = useRef(false)
  const reportedMilestones = useRef(new Set<number>())

  const videoURL = import.meta.env.VITE_PARENT_EXPLAINER_VIDEO_URL
  const showVideo = import.meta.env.VITE_SHOW_VIDEO_ON_REGISTRATION === 'true' && !!videoURL

  // onPlay also fires when resuming after a pause, so only the first one is a start.
  function handlePlay() {
    if (hasStarted.current) return

    hasStarted.current = true
    sendVideoStartEvent(PARENT_EXPLAINER_VIDEO_ID)
  }

  // timeupdate fires several times a second, so each milestone is latched once.
  function handleTimeUpdate(event: SyntheticEvent<HTMLVideoElement>) {
    const { currentTime, duration } = event.currentTarget

    if (!Number.isFinite(duration) || duration <= 0) return

    const percentPlayed = (currentTime / duration) * 100

    PROGRESS_MILESTONES.forEach((milestone) => {
      if (percentPlayed >= milestone && !reportedMilestones.current.has(milestone)) {
        reportedMilestones.current.add(milestone)
        sendVideoProgressEvent(PARENT_EXPLAINER_VIDEO_ID, milestone)
      }
    })
  }

  function handleEnded() {
    if (hasCompleted.current) return

    hasCompleted.current = true
    sendVideoCompleteEvent(PARENT_EXPLAINER_VIDEO_ID)
  }

  return (
    <>
      <div className='text-white font-serif font-bold text-3xl pb-6'>
        {showVideo ? t('content.welcome_page.video_title') : t('content.components.login.title')}
      </div>
      {showVideo ? (
        <video
          className={MEDIA_CLASSES}
          src={videoURL}
          poster={parentExplainerPoster}
          controls
          preload='metadata'
          playsInline
          aria-label={t('content.welcome_page.video_title')}
          onPlay={handlePlay}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
        />
      ) : (
        <img src={registerSplash} alt={'Register'} className={MEDIA_CLASSES} />
      )}
    </>
  )
}
